// §4.1 Stock Lookup (office) — THE FLAGSHIP.
//
// The ≤3-click / ≤15-second search: one search bar (autofocused, `/` hotkey),
// a facility filter chip, and the three ranked §3.8 groups — Units,
// Off-cut combos, Bulk lots — rendered in that order, every result carrying its
// location breadcrumb, qc chip and last-seen stamp so the storekeeper can walk
// straight to the stock. Three clicks: type → (optionally) pick a group action →
// open. Nothing on this screen needs a second screen to answer "have we got it".
//
// §7 visibility: quarantined stock is filtered SERVER-SIDE for callers without
// `inventory.quarantine` / `inventory.count.review` (lookup.service.ts). This
// screen therefore never filters — it only BADGES what it is given amber, which
// is the qc/storekeeper view the spec describes. A client-side filter would be
// a second, weaker rule that could disagree with the server.
//
// Degradation: `degraded[]` (Module 05's off-cut discount pricing) and
// `offcut_rejected[]` ("3 nearby pieces are quarantined") are rendered
// explicitly — an empty combos group is never left to look like "no combos".
//
// Empty state (§4.1 verbatim): "No stock matches '70mm 200m'. Nearest: 3 units
// of 70mm² under 200 m — show anyway?" — a REAL second lookup with the measure
// constraint dropped, issued only when the first came back empty in all three
// groups. See lib/lookup-query.ts for why the client knows which token to drop.

import { enumLabel } from "../../../../lib/enum-label";
import { shortId } from "../../../../lib/short-id";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable } from "../../../../components/data-table";
import { CheckboxField } from "../../../../components/field";
import { EmptyState, ErrorState, Skeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { AdjustStockLink } from "../components/adjust-stock-link";
import { FacilityChip } from "../components/filters";
import {
  InventoryStatus,
  LocationPath,
  RefLabel,
  RemainingBar,
  WaitingOnModule,
} from "../components/inventory-ui";
import { ReservePanel, type ReserveTarget } from "../components/reserve-panel";
import {
  useFacilities,
  useLocationPathIndex,
  useLookup,
  useProductIndex,
  useStockUnitIndex,
  type LookupBulkLot,
  type LookupOffcutCombo,
  type LookupResponse,
  type LookupUnitMatch,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { isAdjustablePlace, placeEntrySearch } from "../lib/adjust";
import { countLookupMatches, isEmptyLookup, splitLookupQuery } from "../lib/lookup-query";
import { formatMeasure, hoursSince, parseMeasure, formatMeasureWithUom } from "../lib/measure";

/**
 * Truncation is the SERVER's statement now (`truncated{}`, §3.8), not this
 * screen's arithmetic.
 *
 * This file used to hold `const GROUP_LIMIT = 50` and infer "the group was cut"
 * from `rows.length >= 50` — a mirror of a server constant, in a client, which
 * says nothing at all about the cap that actually mattered (product resolution,
 * which shrinks the search space before any group is built and can leave every
 * group SHORT rather than full). Both the office and the floor now render the
 * flags the server sends, so the two surfaces can never disagree about whether
 * an answer is complete, and no client has to know a number to be honest.
 */
function TruncationNote({
  truncated,
  testId = "lookup-products-capped",
}: {
  truncated: Pick<LookupResponse["truncated"], "product_resolution">;
  testId?: string;
}) {
  const { t } = useTranslation();
  if (!truncated.product_resolution) return null;
  return (
    <p className="degraded-note" role="note" data-testid={testId}>
      {t("inventory.lookup.productsCapped")}
    </p>
  );
}

export function StockLookupScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();
  const perms = useInventoryPermissions();

  // §4.9's "see held stock" drill link arrives as ?q=<sku>.
  const search = useSearch({ strict: false });
  const [text, setText] = useState(search.q ?? "");
  const [facilityId, setFacilityId] = useState("");
  const [includeReserved, setIncludeReserved] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [reserveTargets, setReserveTargets] = useState<readonly ReserveTarget[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debounced = useDebouncedValue(text, 250);
  const trimmed = debounced.trim();
  const active = trimmed.length > 0;

  // `/` focuses the search bar from anywhere on the screen (§4.1), unless the
  // user is already typing into a field.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      event.preventDefault();
      inputRef.current?.focus();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // `include_reserved` is sent on EVERY query, in both states. The server
  // defaults it to true (`lookup.service.ts`: `params.includeReserved ?? true`),
  // so omitting the key when the box is unchecked left held stock in the ranked
  // results and made the control look broken.
  const primaryQuery = useMemo(() => {
    const query: { q: string; facility_id?: string; include_reserved: boolean } = {
      q: trimmed,
      include_reserved: includeReserved,
    };
    if (facilityId.length > 0) query.facility_id = facilityId;
    return query;
  }, [trimmed, facilityId, includeReserved]);

  const lookup = useLookup(primaryQuery, active);

  // The §4.1 alternative: same query, measure constraint dropped. Only issued
  // when the primary came back empty in all three groups AND the query actually
  // carried a measure token — otherwise there is nothing to relax.
  const split = useMemo(() => splitLookupQuery(trimmed), [trimmed]);
  const primaryEmpty = isEmptyLookup(lookup.data);
  const alternativesPossible =
    primaryEmpty && split.requiredMeasure !== undefined && split.withoutMeasure.length > 0;

  const alternativeQuery = useMemo(() => {
    const query: { q: string; facility_id?: string; include_reserved: boolean } = {
      q: split.withoutMeasure,
      include_reserved: includeReserved,
    };
    if (facilityId.length > 0) query.facility_id = facilityId;
    return query;
  }, [split.withoutMeasure, facilityId, includeReserved]);

  const alternatives = useLookup(alternativeQuery, alternativesPossible);

  // ---- "nothing matched" vs "nothing you can USE matched" (task #194) ------
  //
  // The reserved filter defaults OFF, which is a defensible answer to the
  // BROWSE question ("what can I commit to this order?") and the wrong answer to
  // the LOOKUP one ("where is the drum in my hands?"). A storekeeper types a
  // serial, the unit is held for an order, and the screen used to say nothing
  // matched — hiding the unit BECAUSE of the fact they most needed to learn.
  //
  // The filter's meaning is left alone. What changes is the dead end: when the
  // filtered answer is empty we ask the SAME question again with held stock
  // included, purely to count it, and the empty state then says how many are
  // held and offers the toggle inline. One extra request, only ever on an empty
  // result, and never when the box is already ticked.
  const reservedProbeQuery = useMemo(() => {
    const query: { q: string; facility_id?: string; include_reserved: boolean } = {
      q: trimmed,
      include_reserved: true,
    };
    if (facilityId.length > 0) query.facility_id = facilityId;
    return query;
  }, [trimmed, facilityId]);
  const reservedProbePossible = active && primaryEmpty && !includeReserved;
  const reservedProbe = useLookup(reservedProbeQuery, reservedProbePossible);
  const reservedHidden = reservedProbePossible ? countLookupMatches(reservedProbe.data) : 0;

  useEffect(() => {
    setShowAlternatives(false);
  }, [trimmed, facilityId]);

  useEffect(() => {
    if (lookup.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [lookup.error, navigate]);

  const data: LookupResponse | undefined = lookup.data;
  const productIds = useMemo(() => {
    const ids: string[] = [];
    for (const unit of data?.unit_matches ?? []) ids.push(unit.product_id);
    for (const bulk of data?.bulk_lots ?? []) ids.push(bulk.product_id);
    for (const unit of alternatives.data?.unit_matches ?? []) ids.push(unit.product_id);
    return ids;
  }, [data, alternatives.data]);
  const products = useProductIndex(productIds);

  const canReserve = perms.has("inventory.reservation.manage");
  const canCut = perms.has("inventory.unit.cut");

  function productLabel(productId: string): string | undefined {
    const product = products.byId.get(productId);
    return product === undefined ? undefined : `${product.sku} · ${product.name}`;
  }

  // §4.1's breadcrumb is `WH1 › A › A-03` — which plant that is only matters
  // when the result set spans more than one, and then it matters a lot: Reserve
  // and Cut act on a bobbin that may be 800 km away. The facility name is
  // prefixed only in that case, so a single-plant tenant keeps the short crumb.
  const facilities = useFacilities();
  const facilityNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const facility of facilities.data?.items ?? []) map.set(facility.id, facility.name);
    return map;
  }, [facilities.data]);
  const spansFacilities = useMemo(() => {
    const seen = new Set<string>();
    for (const unit of data?.unit_matches ?? []) seen.add(unit.facility_id);
    return seen.size > 1;
  }, [data]);
  const facilityLabel = (id: string): string | undefined =>
    spansFacilities ? facilityNames.get(id) : undefined;

  // Roving focus over the result cards: `/` puts the caret in the box,
  // ArrowDown walks the ranked results, Enter opens, Shift+Enter reserves,
  // Escape clears. Without this the ≤15-second promise dies on the mouse —
  // a storekeeper on the phone to a customer never leaves the keyboard.
  const resultsRef = useRef<HTMLDivElement>(null);

  function moveFocus(delta: number) {
    const container = resultsRef.current;
    if (container === null) return;
    const items = Array.from(container.querySelectorAll<HTMLElement>("[data-lookup-item]"));
    if (items.length === 0) return;
    const current = items.findIndex((item) => item === document.activeElement);
    const next = current < 0 ? (delta > 0 ? 0 : items.length - 1) : current + delta;
    const clamped = Math.max(0, Math.min(items.length - 1, next));
    items[clamped]?.focus();
  }

  function onResultsKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(-1);
    } else if (event.key === "Escape") {
      setText("");
      inputRef.current?.focus();
    }
  }

  // "Nearest: N units of <query> under <measure> <uom>" — a real count from the
  // alternative response, restricted to units that are genuinely SHORT of the
  // requirement (that is what "nearest under" means).
  const nearestUnder = useMemo(() => {
    if (!alternativesPossible || alternatives.data === undefined) return [];
    // Exact comparison on the scaled integers (D-042) — never Number() on a
    // ledger measure, even for a display filter.
    const required = parseMeasure(split.requiredMeasure);
    if (required === undefined) return [];
    return alternatives.data.unit_matches.filter((unit) => {
      const remaining = parseMeasure(unit.remaining_measure);
      return remaining !== undefined && remaining < required;
    });
  }, [alternativesPossible, alternatives.data, split.requiredMeasure]);

  return (
    <section className="stock-lookup" data-testid="lookup-screen" onKeyDown={onResultsKeyDown}>
      <PageHeader
        title={t("inventory.lookup.title")}
        actions={
          <>
            {/* §4.12 (D-315): the storekeeper lands here, so the form's first
            door is here. Shown only to `inventory.adjust` holders. */}
            <AdjustStockLink
              search={{ from: "lookup" }}
              label={t("inventory.lookup.adjust")}
              testId="lookup-adjust"
            />
          </>
        }
      />

      <div className="lookup-bar">
        <label className="lookup-search">
          <span className="visually-hidden">{t("inventory.lookup.searchLabel")}</span>
          <input
            ref={inputRef}
            data-testid="lookup-input"
            autoFocus
            type="search"
            value={text}
            onChange={(event) => {
              setText(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                // Handled here, not by the section: letting it bubble would
                // step the roving focus twice.
                event.preventDefault();
                event.stopPropagation();
                moveFocus(1);
              }
            }}
            placeholder={t("inventory.lookup.placeholder")}
            aria-describedby="lookup-hotkey-hint"
          />
          {/* Rev-7: the shortcut is a key chip inside the box rather than a
              line that sat below the input's baseline; the sentence stays for
              screen readers through aria-describedby. */}
          <kbd className="lookup-kbd" aria-hidden="true">
            /
          </kbd>
          <span id="lookup-hotkey-hint" className="visually-hidden">
            {t("inventory.lookup.hotkeyHint")}
          </span>
        </label>
        <FacilityChip value={facilityId} onChange={setFacilityId} />
        <CheckboxField
          data-testid="lookup-include-reserved"
          label={t("inventory.lookup.includeReserved")}
          checked={includeReserved}
          onChange={setIncludeReserved}
        />
      </div>

      {!active ? (
        <EmptyState
          title={t("inventory.lookup.idleTitle")}
          body={t("inventory.lookup.idleBody", { unit: term("stock_unit").toLowerCase() })}
        />
      ) : null}

      {active && lookup.isPending ? (
        <div className="lookup-skeletons" role="status" aria-label={t("inventory.lookup.loading")}>
          {[0, 1, 2].map((row) => (
            <div className="lookup-card skeleton-card" key={row}>
              <Skeleton width="45%" height="1.2rem" />
              <Skeleton height="0.9rem" />
              <Skeleton width="70%" height="0.9rem" />
            </div>
          ))}
        </div>
      ) : null}

      {active && lookup.error && !(lookup.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("inventory.lookup.errorTitle")}
          detail={lookup.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void lookup.refetch()}
          requestId={requestIdOf(lookup.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : null}

      {data !== undefined && !primaryEmpty ? (
        <div ref={resultsRef} data-testid="lookup-results">
          {(data.degraded ?? []).length > 0 ? (
            <p className="degraded-note" role="note">
              {t("inventory.lookup.degradedIntro")}{" "}
              {(data.degraded ?? []).map((entry) => (
                <span key={entry.term} className="degraded-item">
                  {enumLabel(t, "inventory.lookup.term", entry.term)}:{" "}
                  <WaitingOnModule owner={entry.owner_module} ownerCode={entry.owner_module_code} />
                </span>
              ))}
            </p>
          ) : null}
          <TruncationNote truncated={data.truncated} />
          <p className="inv-hint">{t("inventory.lookup.keyboardHint")}</p>

          <UnitGroup
            title={t("inventory.lookup.groups.units", { unit: term("stock_unit") })}
            units={data.unit_matches}
            capped={data.truncated.unit_matches}
            productLabel={productLabel}
            productsPending={products.isPending}
            facilityLabel={facilityLabel}
            locale={i18n.language}
            canReserve={canReserve}
            canCut={canCut}
            onReserve={setReserveTargets}
          />

          <ComboGroup
            title={t("inventory.lookup.groups.combos", { offcut: term("off_cut") })}
            combos={data.offcut_combos}
            eligibleCount={data.offcut_eligible_count}
            candidatesCapped={data.truncated.offcut_candidates}
            rejected={data.offcut_rejected ?? []}
            locale={i18n.language}
            uom={split.uom}
            canReserve={canReserve}
            onReserve={setReserveTargets}
          />

          <BulkGroup
            title={t("inventory.lookup.groups.bulk", { lot: term("lot") })}
            bulk={data.bulk_lots}
            capped={data.truncated.bulk_lots}
            productLabel={productLabel}
            productsPending={products.isPending}
            locale={i18n.language}
          />
        </div>
      ) : null}

      {data !== undefined && primaryEmpty ? (
        <div
          className="lookup-empty"
          data-testid="lookup-empty"
          data-reserved-hidden={reservedHidden > 0 ? String(reservedHidden) : undefined}
        >
          {/* An empty answer is exactly where a cut search does its damage:
              "no stock" and "we did not look at all of it" read identically.
              The note comes FIRST, above the empty state's copy. */}
          <TruncationNote truncated={data.truncated} />
          <EmptyState
            title={
              reservedHidden > 0
                ? t("inventory.lookup.emptyReservedTitle", { query: trimmed })
                : t("inventory.lookup.emptyTitle", { query: trimmed })
            }
            body={
              // HELD STOCK WINS THE EXPLANATION. "There are 3 of these and they
              // are all spoken for" answers the question a dead end raised; a
              // shorter alternative under a different measure does not, and
              // offering it first would talk past someone holding the very unit
              // the filter is hiding.
              reservedHidden > 0
                ? t("inventory.lookup.emptyReservedBody", { count: reservedHidden })
                : reservedProbePossible && reservedProbe.isPending
                  ? t("inventory.lookup.emptyChecking")
                  : alternativesPossible
                    ? alternatives.isPending
                      ? t("inventory.lookup.emptyChecking")
                      : nearestUnder.length > 0
                        ? t("inventory.lookup.emptyNearest", {
                            n: nearestUnder.length,
                            product: split.withoutMeasure,
                            measure: formatMeasure(split.requiredMeasure, {
                              locale: i18n.language,
                            }),
                            uom: split.uom ?? "",
                          })
                        : t("inventory.lookup.emptyNoNearest")
                    : t("inventory.lookup.emptyBody")
            }
            {...(reservedHidden > 0
              ? {
                  ctaLabel: t("inventory.lookup.showReserved"),
                  onCta: () => {
                    setIncludeReserved(true);
                  },
                }
              : alternativesPossible && !alternatives.isPending && nearestUnder.length > 0
                ? {
                    ctaLabel: showAlternatives
                      ? t("inventory.lookup.hideAlternatives")
                      : t("inventory.lookup.showAnyway"),
                    onCta: () => {
                      setShowAlternatives((previous) => !previous);
                    },
                  }
                : {})}
          />
          {showAlternatives && nearestUnder.length > 0 ? (
            <div ref={resultsRef}>
              {/* The relaxed search is a SECOND query with its own bounds. Its
                  `unit_matches` cap is passed to the group below, but a
                  `product_resolution` truncation on THIS call is a different
                  fact from one on the primary — without its own note a cut
                  "nearest" list reads as the complete set of near misses. */}
              <TruncationNote
                truncated={alternatives.data?.truncated ?? { product_resolution: false }}
                testId="lookup-alternatives-capped"
              />
              <UnitGroup
                title={t("inventory.lookup.groups.nearest", {
                  measure: formatMeasure(split.requiredMeasure, { locale: i18n.language }),
                  uom: split.uom ?? "",
                })}
                units={nearestUnder}
                capped={alternatives.data?.truncated.unit_matches ?? false}
                productLabel={productLabel}
                productsPending={products.isPending}
                facilityLabel={facilityLabel}
                locale={i18n.language}
                canReserve={canReserve}
                canCut={canCut}
                onReserve={setReserveTargets}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {reserveTargets !== null ? (
        <ReservePanel
          targets={reserveTargets}
          onClose={() => {
            setReserveTargets(null);
          }}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Group 1 — units (smallest-sufficient-first, §3.8)
// ---------------------------------------------------------------------------

function UnitGroup({
  title,
  units,
  capped,
  productLabel,
  productsPending,
  facilityLabel,
  locale,
  canReserve,
  canCut,
  onReserve,
}: {
  title: string;
  units: readonly LookupUnitMatch[];
  /** The SERVER said this group was cut (§3.8 `truncated.unit_matches`). */
  capped: boolean;
  productLabel: (id: string) => string | undefined;
  productsPending: boolean;
  facilityLabel: (id: string) => string | undefined;
  locale: string;
  canReserve: boolean;
  canCut: boolean;
  onReserve: (targets: readonly ReserveTarget[]) => void;
}) {
  const { t } = useTranslation();
  if (units.length === 0) return null;

  function reserveTargetFor(unit: LookupUnitMatch): ReserveTarget {
    return {
      productId: unit.product_id,
      facilityId: unit.facility_id,
      uom: unit.uom,
      quantity: unit.remaining_measure,
      stockUnitId: unit.stock_unit_id,
      ...(unit.lot_id !== null ? { lotId: unit.lot_id } : {}),
      label: `${unit.serial_code} · ${formatMeasureWithUom(unit.remaining_measure, unit.uom, { locale })}`,
    };
  }

  return (
    <section className="lookup-group" data-testid="lookup-units" aria-label={title}>
      <h2>{title}</h2>
      {capped ? (
        <p className="degraded-note" role="note" data-testid="lookup-units-capped">
          {t("inventory.lookup.groupCapped")}
        </p>
      ) : null}
      <ul className="lookup-cards">
        {units.map((unit) => {
          const hours = hoursSince(unit.last_scanned_at);
          const facility = facilityLabel(unit.facility_id);
          return (
            <li
              className="lookup-card"
              data-testid="lookup-unit"
              data-serial={unit.serial_code}
              data-remaining={unit.remaining_measure}
              data-location={unit.location_path ?? ""}
              data-qc={unit.lot_qc_status ?? ""}
              data-reserved={String(unit.reserved)}
              key={unit.stock_unit_id}
            >
              <div className="lookup-card-head">
                <Link
                  to="/inventory/units/$id"
                  params={{ id: unit.stock_unit_id }}
                  className="lookup-serial"
                  data-lookup-item
                  onKeyDown={(event) => {
                    // Shift+Enter reserves straight from the ranked list; plain
                    // Enter is the link's own "open".
                    if (event.key === "Enter" && event.shiftKey && canReserve) {
                      event.preventDefault();
                      onReserve([reserveTargetFor(unit)]);
                    }
                  }}
                >
                  {unit.serial_code}
                </Link>
                {unit.lot_qc_status !== null && unit.lot_qc_status !== "released" ? (
                  <InventoryStatus status={unit.lot_qc_status} scope="qc" />
                ) : null}
                {unit.reserved ? (
                  <span className="status-badge status-neutral" data-testid="lookup-reserved-badge">
                    {/* §3.8 rank 1 / M06-S12: a hold is only actionable when it
                        names the demand it is for — "Reserved · SO-1001", not a
                        bare "Reserved". Since D-151 the demand may carry the
                        WORDS somebody typed ("BuildCo · Thursday"), which is
                        what a storekeeper can act on; the id's first eight hex
                        characters remain the fallback for the module-minted
                        kinds, which have no words to show. */}
                    {unit.reservation_demand_ref === null
                      ? t("inventory.lookup.reserved")
                      : t("inventory.lookup.reservedFor", {
                          demand: enumLabel(
                            t,
                            "inventory.reserve.kind",
                            unit.reservation_demand_ref.demand_type,
                          ),
                          id:
                            unit.reservation_demand_ref.demand_ref ??
                            shortId(unit.reservation_demand_ref.demand_id),
                        })}
                  </span>
                ) : null}
              </div>
              <p className="lookup-product">
                <RefLabel
                  value={productLabel(unit.product_id)}
                  id={unit.product_id}
                  pending={productsPending}
                  kind="product"
                />
              </p>
              <RemainingBar
                remaining={unit.remaining_measure}
                capacity={unit.capacity_measure}
                uom={unit.uom}
              />
              <p className="lookup-meta">
                {facility !== undefined ? (
                  <span className="loc-facility">{facility} · </span>
                ) : null}
                <LocationPath path={unit.location_path} />
              </p>
              <p className="lookup-meta lookup-lastseen">
                {hours === undefined
                  ? t("inventory.lookup.neverScanned")
                  : hours < 1
                    ? t("inventory.lookup.lastSeenMinutes", {
                        n: Math.max(1, Math.round(hours * 60)),
                      })
                    : hours < 48
                      ? t("inventory.lookup.lastSeenHours", { n: Math.round(hours) })
                      : t("inventory.lookup.lastSeenDays", { n: Math.round(hours / 24) })}
              </p>
              <div className="lookup-actions">
                {canReserve ? (
                  <Button
                    onClick={() => {
                      onReserve([reserveTargetFor(unit)]);
                    }}
                  >
                    {t("inventory.lookup.reserve")}
                  </Button>
                ) : null}
                {canCut ? (
                  <Link
                    to="/inventory/units/$id"
                    params={{ id: unit.stock_unit_id }}
                    hash="cut"
                    className="ds-lnk"
                  >
                    {t("inventory.lookup.cut")}
                  </Link>
                ) : null}
                {/* §4.1's third action is "Locate" (print/ping) — a FLOOR
                    capability that needs the label printer / device ping the
                    floor slice owns. The office answer to "where is it" is the
                    breadcrumb above, so this row opens the unit card rather
                    than offering a button that would do nothing. §4.1 amended
                    in this PR to record it. */}
                <Link
                  to="/inventory/units/$id"
                  params={{ id: unit.stock_unit_id }}
                  className="ds-lnk"
                >
                  {t("inventory.lookup.open")}
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Group 2 — off-cut combinations (kernel matcher, §3.8 / D-047 pt 5)
// ---------------------------------------------------------------------------

/** Exported for the capped-empty test: this group is pure presentation over a
 * server answer, and the rule it has to get right — never assert "no pair
 * covers this length" over a TRUNCATED search — is provable by rendering it
 * directly. Driving the whole screen to reach it would need the transport, the
 * permission feed and the router mocked, and the mocks would be the thing under
 * test. */
export function ComboGroup({
  title,
  combos,
  eligibleCount,
  candidatesCapped,
  rejected,
  locale,
  uom,
  canReserve,
  onReserve,
}: {
  title: string;
  combos: readonly LookupOffcutCombo[];
  eligibleCount: number | undefined;
  /** The candidate SCAN was cut (§3.8 `truncated.offcut_candidates`), so
   * `eligibleCount` counts what was looked at, not what is in the yard. Saying
   * "2 pieces are eligible" over a cut scan is a number that reads exact and
   * is not. */
  candidatesCapped: boolean;
  rejected: readonly { stock_unit_id: string | null; reason: string }[];
  locale: string;
  /** The unit the search named its length in ("200 m"). A combo exists only
   * for such a search, so every length on the card carries this unit. */
  uom?: string | undefined;
  canReserve: boolean;
  onReserve: (targets: readonly ReserveTarget[]) => void;
}) {
  const { t } = useTranslation();
  // A combo piece carries only its id/serial/remaining/location (§5's
  // `LookupOffcutCombo`), and a hold needs product + facility + uom — so the
  // pieces on screen are resolved through the same per-id unit index the rest
  // of the area uses, and "Reserve both" stays disabled until they are known.
  // Guessing those fields would post a hold against the wrong product.
  // Resolved only for a caller who can actually act on them — this is one read
  // per piece, and a role that cannot reserve has no use for the result.
  const pieceIds = canReserve
    ? combos.flatMap((combo) => combo.pieces.map((piece) => piece.stock_unit_id))
    : [];
  const units = useStockUnitIndex(pieceIds);

  // The group is only meaningful when the query named a length to cover; the
  // server signals that by sending the eligible count at all.
  if (eligibleCount === undefined && combos.length === 0) return null;

  function targetsFor(combo: LookupOffcutCombo): readonly ReserveTarget[] | undefined {
    const out: ReserveTarget[] = [];
    for (const piece of combo.pieces) {
      const unit = units.byId.get(piece.stock_unit_id);
      if (unit?.product_id == null) return undefined;
      out.push({
        productId: unit.product_id,
        facilityId: unit.facility_id,
        uom: unit.measure_uom ?? "",
        quantity: piece.remaining_measure,
        stockUnitId: piece.stock_unit_id,
        ...(unit.lot_id !== null ? { lotId: unit.lot_id } : {}),
        label: `${piece.serial_code} · ${formatMeasureWithUom(piece.remaining_measure, uom, { locale })}`,
      });
    }
    return out;
  }

  const byReason = new Map<string, number>();
  for (const entry of rejected) byReason.set(entry.reason, (byReason.get(entry.reason) ?? 0) + 1);

  return (
    <section className="lookup-group" data-testid="lookup-combos" aria-label={title}>
      <h2>{title}</h2>
      {candidatesCapped ? (
        <p className="degraded-note" role="note" data-testid="lookup-combos-capped">
          {t("inventory.lookup.candidatesCapped")}
        </p>
      ) : null}
      {combos.length === 0 ? (
        // A CAPPED SEARCH THAT FOUND NOTHING HAS NOT PROVED NOTHING EXISTS.
        // `truncated.offcut_candidates` is the server saying its candidate
        // window filled before it ran out of pieces, so "no pair covers this
        // length" would be absence of evidence dressed as evidence of absence
        // — and a planner acts on that sentence by cutting a full drum. The
        // capped NOTE above is not enough on its own: it explains the eligible
        // COUNT, and the reader's eye goes to the bold negative underneath it.
        <p className="lookup-note" data-testid="lookup-combos-empty">
          {candidatesCapped ? t("inventory.lookup.noCombosCapped") : t("inventory.lookup.noCombos")}
        </p>
      ) : (
        <ul className="lookup-cards">
          {combos.map((combo) => (
            <li
              className="lookup-card"
              data-testid="lookup-combo"
              data-serials={combo.pieces.map((p) => p.serial_code).join("+")}
              data-total={combo.combined_measure}
              data-surplus={combo.surplus}
              key={combo.pieces.map((p) => p.stock_unit_id).join("+")}
            >
              <div className="combo-pieces">
                {combo.pieces.map((piece, index) => (
                  <span key={piece.stock_unit_id} className="combo-piece">
                    {index > 0 ? (
                      <span className="combo-plus" aria-hidden="true">
                        +
                      </span>
                    ) : null}
                    <Link to="/inventory/units/$id" params={{ id: piece.stock_unit_id }}>
                      {piece.serial_code}
                    </Link>
                    <span className="combo-len">
                      {formatMeasureWithUom(piece.remaining_measure, uom, { locale })}
                    </span>
                    <LocationPath path={piece.location_path} />
                  </span>
                ))}
              </div>
              <p className="combo-total">
                {t("inventory.lookup.comboTotal", {
                  total: formatMeasureWithUom(combo.combined_measure, uom, { locale }),
                  surplus: formatMeasureWithUom(combo.surplus, uom, { locale }),
                })}
              </p>
              <p className="lookup-meta">
                {combo.discount_source === "unavailable" || combo.discount_hint === null ? (
                  <>
                    {t("inventory.lookup.discountLabel")} <WaitingOnModule ownerCode="05" />
                  </>
                ) : (
                  <>
                    {t("inventory.lookup.discountLabel")} {combo.discount_hint}
                  </>
                )}
              </p>
              {canReserve ? (
                <div className="lookup-actions">
                  {/* §4.1 / M06-S02: one demand reference, one hold per piece. */}
                  <Button
                    data-testid="lookup-combo-reserve"
                    disabled={targetsFor(combo) === undefined}
                    title={
                      targetsFor(combo) === undefined ? t("inventory.lookup.resolving") : undefined
                    }
                    onClick={() => {
                      const targets = targetsFor(combo);
                      if (targets !== undefined) onReserve(targets);
                    }}
                  >
                    {t("inventory.lookup.reserveBoth", { n: combo.pieces.length })}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {byReason.size > 0 ? (
        <p className="lookup-note" role="note">
          {Array.from(byReason.entries()).map(([reason, count]) => (
            <span key={reason} className="reject-reason">
              {t(`inventory.lookup.rejected.${reason}`, { n: count, defaultValue: reason })}
            </span>
          ))}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Group 3 — bulk lots (§3.8)
// ---------------------------------------------------------------------------

function BulkGroup({
  title,
  bulk,
  capped,
  productLabel,
  productsPending,
  locale,
}: {
  title: string;
  bulk: readonly LookupBulkLot[];
  /** The SERVER said this group was cut (§3.8 `truncated.bulk_lots`). */
  capped: boolean;
  productLabel: (id: string) => string | undefined;
  productsPending: boolean;
  locale: string;
}) {
  const { t } = useTranslation();
  const canAdjust = useInventoryPermissions().has("inventory.adjust");
  // §4.12: only a place that holds stock takes the entry; a bulk row can be
  // the plant's work in progress, which the form books against.
  const places = useLocationPathIndex(bulk.map((row) => row.location_id));
  if (bulk.length === 0) return null;
  return (
    <section className="lookup-group" aria-label={title}>
      <h2>{title}</h2>
      {capped ? (
        <p className="degraded-note" role="note" data-testid="lookup-bulk-capped">
          {t("inventory.lookup.groupCapped")}
        </p>
      ) : null}
      <DataTable
        columns={[
          {
            key: "lot_code",
            header: t("inventory.lookup.colLot"),
            id: true,
            render: (row) => (
              <Link to="/inventory/lots/$id" params={{ id: row.lot_id }} className="ds-lnk">
                {row.lot_code}
              </Link>
            ),
          },
          {
            key: "product_id",
            header: t("inventory.lookup.colProduct"),
            render: (row) => (
              <RefLabel
                value={productLabel(row.product_id)}
                id={row.product_id}
                pending={productsPending}
                kind="product"
              />
            ),
          },
          {
            key: "on_hand",
            header: t("inventory.lookup.colOnHand"),
            numeric: true,
            render: (row) => formatMeasureWithUom(row.on_hand, row.uom, { locale }),
          },
          {
            key: "location_path",
            header: t("inventory.lookup.colLocation"),
            render: (row) => <LocationPath path={row.location_path} />,
          },
          // §4.12: a bulk row is loose stock at one place — exactly what a
          // stock adjustment corrects, so the row opens the form on it.
          ...(canAdjust
            ? [
                {
                  key: "adjust",
                  header: t("inventory.common.actions"),
                  actions: true,
                  render: (row: LookupBulkLot) =>
                    !isAdjustablePlace(places.byId.get(row.location_id)?.kind) ? null : (
                      <AdjustStockLink
                        search={{
                          product_id: row.product_id,
                          lot_id: row.lot_id,
                          location_id: row.location_id,
                          ...placeEntrySearch(places.byId.get(row.location_id)?.kind),
                          from: "lookup",
                        }}
                        label={t("inventory.lookup.adjustRow")}
                        testId={`lookup-bulk-adjust-${row.lot_id}-${row.location_id}`}
                      />
                    ),
                },
              ]
            : []),
        ]}
        rows={bulk}
        rowKey={(row) => `${row.lot_id}-${row.location_id}`}
        empty={null}
      />
    </section>
  );
}
